/**
 * Phase 8 · C-39 — Turn-around time reports service.
 *
 * Powers `/reports/tat` (ReportsTatPage) with three views over the same
 * underlying requisition data:
 *
 *   • By position   — one row per reportable requisition, enriched with a
 *                     human-readable `stageLabel`.
 *   • By individual — aggregates per owner: active positions, avg days
 *                     open, avg TAT, breach %, on-time-fill %, 7d spark.
 *   • By hierarchy  — single-root tree (org → individuals). No real
 *                     hierarchy table exists in this codebase (settings /
 *                     OrgHierarchyPage synthesises the tree from roles),
 *                     so per the spec we return a flat `org → [individuals]`
 *                     shape. Team-level rollup can slot in later once a
 *                     manager_id or org_hierarchy table lands.
 *
 * The "owner" of a position is the `approver_user_id` on the requisition
 * (= `ggj_b2b_org_users.id`). The C-39 spec abbreviates this as
 * `created_by = owner`, but `created_by` on this table is a Keycloak sub
 * (text) — we deliberately group by the org_user FK to match
 * `positionTatService`'s ownership model and keep report cells joinable
 * against team-activity data.
 *
 * Implementation notes:
 *   - We read live rows through `positionTatService.listByOrg` (no range)
 *     and do per-metric filtering client-side. This keeps the DB round-
 *     trip single and avoids pre-aggregation tables per the spec. The
 *     row volume is bounded by reportable statuses (sourcing / filled /
 *     cancelled) so memory stays tiny in practice.
 *   - `spark7d` is computed client-side from the same row set — 7 daily
 *     snapshots of active-open count per owner. No extra queries needed.
 *   - DateRange presets are resolved into concrete ISO bounds inside this
 *     file so UI callers just pass `{ preset: '30d' }` and get identical
 *     window semantics across the three list functions.
 */

import { supabase } from '../client';
import { positionTatService, type PositionTatRow as BaseTatRow } from './positionTatService';
import { REQUISITION_STATUS_LABELS } from '@core/constants/requisitionLifecycle';
import type { RequisitionStatus } from '@core/types';

// ── Public types ────────────────────────────────────────────────────────────

export interface DateRange {
  preset: '7d' | '30d' | '90d' | 'custom';
  /** ISO 8601 timestamp. Only honoured when `preset === 'custom'`; otherwise derived from preset. */
  from?: string;
  /** ISO 8601 timestamp. Same rule as `from`. */
  to?: string;
}

/**
 * Per-position TAT row. Extends the base `PositionTatRow` from
 * `positionTatService` with a display-ready `stageLabel` so the report
 * table doesn't have to import the lifecycle constants itself.
 */
export interface PositionTatRow extends BaseTatRow {
  /** Human-readable stage label (e.g. 'Sourcing (recruiting active)'). */
  stageLabel: string;
}

export interface IndividualTatRow {
  userId: string;
  userName: string;
  avatarUrl: string | null;
  activePositions: number;
  /** AVG days open across currently-open positions. 0 when none open. */
  avgDaysOpen: number;
  /** AVG actual TAT (days) across closed positions in range. 0 when none closed in range. */
  avgTatActual: number;
  /** Fraction 0..1, across active + range-bounded closed positions. */
  breachRate: number;
  /** Fraction 0..1, closed-in-range positions filled on or before target_close_date. */
  onTimeFillRate: number;
  /** Oldest-first daily active-open counts for the last 7 days. */
  spark7d: number[];
}

export type HierarchyNodeMetrics = Omit<
  IndividualTatRow,
  'userId' | 'userName' | 'avatarUrl' | 'spark7d'
>;

export interface HierarchyTatNode {
  kind: 'org' | 'team' | 'individual';
  id: string;
  label: string;
  metrics: HierarchyNodeMetrics;
  children?: HierarchyTatNode[];
}

/**
 * C-40 · Position timeline drawer.
 *
 * A single merged, chronologically-descending stream of everything that
 * happened to a requisition, synthesised live from five source tables:
 *
 *   • ggj_b2b_object_history          (requisition field edits + status)
 *   • ggj_b2b_applications            (candidates attached to the req)
 *   • ggj_b2b_application_status_history (shortlist / reject / stage)
 *   • ggj_b2b_interview_events        (scheduled / held / outcome)
 *   • ggj_b2b_ai_interview_sessions   (AI screening completions)
 *   • ggj_b2b_outbox_events           (email.sent / email.sequence.enrolled)
 *
 * No materialised table — every read joins the live rows. Per-source
 * failures log and continue so the drawer always renders.
 */
export type TimelineEventKind =
  | 'position.opened'
  | 'position.status_changed'
  | 'position.field_edited'
  | 'application.added'
  | 'application.shortlisted'
  | 'application.rejected'
  | 'application.stage_changed'
  | 'interview.scheduled'
  | 'interview.held'
  | 'interview.feedback'
  | 'interview.outcome'
  | 'ai_interview.completed'
  | 'email.sent'
  | 'email.sequence.enrolled'
  | 'position.closed';

export interface TimelineEvent {
  /** Stable per-source-row id. Composite of `<source>:<rowId>:<kind>` so
   *  one DB row can surface as two events without colliding. */
  id: string;
  /** ISO 8601 event time. */
  ts: string;
  kind: TimelineEventKind;
  /** `ggj_b2b_org_users.id` when known, else null (system-driven events). */
  actorUserId: string | null;
  actorName: string | null;
  actorAvatarUrl: string | null;
  /** Short human phrase: "Shortlisted: Jane Doe" / "Status: draft → sourcing". */
  label: string;
  /** Optional longer explanation (old→new values, free-text). */
  detail?: string;
  /** Deep link to the source record; omitted when no useful destination. */
  viewHref?: string;
  icon: 'plus' | 'check' | 'x' | 'mail' | 'phone' | 'calendar' | 'edit' | 'sparkles';
}

export interface PositionTimelineOpts {
  /** Max events returned. Default 200 per spec. */
  limit?: number;
  /** ISO 8601 — when set, only events with `ts < before` are returned
   *  (infinite scroll for older events). */
  before?: string;
}

// ── Internal helpers ────────────────────────────────────────────────────────

const MS_PER_DAY = 86_400_000;

/** Resolve `DateRange` into concrete `{ fromIso, toIso }` bounds. */
function resolveRange(range: DateRange): { fromIso: string; toIso: string } {
  const toIso = range.preset === 'custom' && range.to ? range.to : new Date().toISOString();
  const to = new Date(toIso).getTime();

  if (range.preset === 'custom' && range.from) {
    return { fromIso: range.from, toIso };
  }

  const days = range.preset === '7d' ? 7 : range.preset === '90d' ? 90 : 30;
  return { fromIso: new Date(to - days * MS_PER_DAY).toISOString(), toIso };
}

/** True when `iso` falls within [fromIso, toIso] inclusive. `null` → false. */
function inRange(iso: string | null, fromIso: string, toIso: string): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= new Date(fromIso).getTime() && t <= new Date(toIso).getTime();
}

/** End-of-day UTC ms for `date` (the moment used for "active-as-of" checks). */
function endOfDayUtc(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999);
}

/** De-dupe + drop null/undefined/empty strings. Used across timeline joins
 *  where the same id can arrive from multiple source rows. */
function uniqueStrings(values: Array<string | null | undefined>): string[] {
  const set = new Set<string>();
  for (const v of values) {
    if (v && v.length > 0) set.add(v);
  }
  return Array.from(set);
}

/** Day-granularity diff in days between two ISO dates (b - a). */
function daysBetween(aIso: string, bIso: string): number {
  const a = new Date(aIso).getTime();
  const b = new Date(bIso).getTime();
  return Math.floor((b - a) / MS_PER_DAY);
}

/**
 * True when a position was "active-open" at `asOfMs` — i.e. it was opened
 * on or before that moment and had not yet closed. Closed positions with
 * `closedAt > asOfMs` still count as active at that point in time.
 */
function wasActiveAt(row: BaseTatRow, asOfMs: number): boolean {
  if (!row.openedAt) return false;
  if (new Date(row.openedAt).getTime() > asOfMs) return false;
  if (row.closedAt && new Date(row.closedAt).getTime() <= asOfMs) return false;
  return true;
}

interface OwnerProfileRow {
  id: string;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

/** Fetch display-name + avatar for a set of owner ids in one round-trip. */
async function fetchOwnerProfiles(
  orgId: string,
  ownerIds: string[],
): Promise<Map<string, { name: string; avatarUrl: string | null }>> {
  const out = new Map<string, { name: string; avatarUrl: string | null }>();
  if (ownerIds.length === 0) return out;

  const { data, error } = await supabase
    .from('ggj_b2b_org_users')
    .select('id, display_name, email, avatar_url')
    .eq('org_id', orgId)
    .in('id', ownerIds);

  if (error) throw new Error(error.message);

  for (const u of (data ?? []) as OwnerProfileRow[]) {
    out.set(u.id, {
      name: u.display_name ?? u.email ?? 'Unknown',
      avatarUrl: u.avatar_url,
    });
  }
  return out;
}

/** Aggregate one owner's metrics from their slice of the row set. */
function aggregateOwner(
  rows: BaseTatRow[],
  dayEnds: number[],
  fromIso: string,
  toIso: string,
): {
  activePositions: number;
  avgDaysOpen: number;
  avgTatActual: number;
  breachRate: number;
  onTimeFillRate: number;
  spark7d: number[];
} {
  const activeRows = rows.filter((r) => r.closedAt === null);
  const closedInRange = rows.filter((r) => inRange(r.closedAt, fromIso, toIso));

  const avgDaysOpen =
    activeRows.length === 0
      ? 0
      : Math.round(
          activeRows.reduce((sum, r) => sum + (r.tatDaysActual ?? 0), 0) / activeRows.length,
        );

  const avgTatActual =
    closedInRange.length === 0
      ? 0
      : Math.round(
          closedInRange.reduce((sum, r) => sum + (r.tatDaysActual ?? 0), 0) / closedInRange.length,
        );

  // breachRate denominator: active + range-bounded closed (per spec).
  const breachPopulation = activeRows.length + closedInRange.length;
  const breachCount =
    activeRows.filter((r) => r.tatBreach).length +
    closedInRange.filter((r) => r.targetCloseDate && r.closedAt && new Date(r.closedAt) > new Date(r.targetCloseDate)).length;
  const breachRate = breachPopulation === 0 ? 0 : breachCount / breachPopulation;

  // onTimeFillRate denominator: closed-in-range with a target date set.
  const fillPopulation = closedInRange.filter((r) => r.targetCloseDate).length;
  const fillOnTime = closedInRange.filter(
    (r) =>
      r.targetCloseDate &&
      r.closedAt &&
      new Date(r.closedAt) <= new Date(r.targetCloseDate),
  ).length;
  const onTimeFillRate = fillPopulation === 0 ? 0 : fillOnTime / fillPopulation;

  const spark7d = dayEnds.map((asOfMs) => rows.filter((r) => wasActiveAt(r, asOfMs)).length);

  return {
    activePositions: activeRows.length,
    avgDaysOpen,
    avgTatActual,
    breachRate,
    onTimeFillRate,
    spark7d,
  };
}

/** Merge individual metrics into an org-level aggregate for the hierarchy root. */
function rollupOrgMetrics(individuals: IndividualTatRow[]): HierarchyNodeMetrics {
  if (individuals.length === 0) {
    return { activePositions: 0, avgDaysOpen: 0, avgTatActual: 0, breachRate: 0, onTimeFillRate: 0 };
  }
  const activePositions = individuals.reduce((s, i) => s + i.activePositions, 0);
  const avg = (key: 'avgDaysOpen' | 'avgTatActual' | 'breachRate' | 'onTimeFillRate') =>
    individuals.reduce((s, i) => s + i[key], 0) / individuals.length;
  return {
    activePositions,
    avgDaysOpen: Math.round(avg('avgDaysOpen')),
    avgTatActual: Math.round(avg('avgTatActual')),
    breachRate: avg('breachRate'),
    onTimeFillRate: avg('onTimeFillRate'),
  };
}

// ── Timeline helpers (C-40) ─────────────────────────────────────────────────

/** Attribute names on `ggj_b2b_requisitions` that already have a dedicated
 *  timeline kind (status / open / close). We skip them in the generic
 *  `position.field_edited` mapping to avoid duplicate rows. */
const REQUISITION_DEDICATED_ATTRS = new Set([
  'status',
  'opened_at',
  'closed_at',
  'id',
  'org_id',
  'created_by',
]);

/** Attributes that are noisy / machine-managed and not interesting in a
 *  user-facing timeline. */
const REQUISITION_SKIP_ATTRS = new Set([
  'updated_at',
  'created_at',
  'job_id',
  'created_job_id',
  'approver_user_id',
]);

/** Human-readable attribute → label. Falls back to the raw column name. */
const REQUISITION_ATTR_LABELS: Record<string, string> = {
  title: 'Title',
  department: 'Department',
  headcount: 'Headcount',
  priority: 'Priority',
  hiring_manager_name: 'Hiring manager',
  hiring_manager_email: 'Hiring manager email',
  budget_min: 'Budget (min)',
  budget_max: 'Budget (max)',
  location: 'Location',
  employment_type: 'Employment type',
  business_justification: 'Business justification',
  must_have_skills: 'Must-have skills',
  nice_to_have_skills: 'Nice-to-have skills',
  internal_notes: 'Internal notes',
  target_start_date: 'Target start date',
  target_close_date: 'Target close date',
  approval_notes: 'Approval notes',
  client_id: 'Client',
};

function statusTransitionLabel(to: string): string {
  return REQUISITION_STATUS_LABELS[to as RequisitionStatus] ?? to.replace(/_/g, ' ');
}

/** Internal row shape for a merge-sort candidate. */
interface TimelineRowSource {
  id: string;
  ts: string;
  kind: TimelineEventKind;
  actorOrgUserId?: string | null;
  actorKeycloakSub?: string | null;
  label: string;
  detail?: string;
  viewHref?: string;
  icon: TimelineEvent['icon'];
}

interface ObjectHistoryRowLite {
  id: string;
  record_id: string;
  action: 'insert' | 'update' | 'delete';
  attribute_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_by_org_user_id: string | null;
  created_at: string;
}

interface ApplicationRowLite {
  id: string;
  job_id: string | null;
  candidate_id: string | null;
  status: string | null;
  created_at: string | null;
  assigned_to: string | null;
}

interface AppStatusHistoryRowLite {
  id: string;
  application_id: string;
  from_status: string | null;
  to_status: string | null;
  changed_by: string | null; // keycloak_sub (TEXT per migration 095)
  changed_at: string | null;
}

interface InterviewEventRowLite {
  id: string;
  application_id: string;
  round: number | null;
  scheduled_at: string | null;
  status: string | null;
  created_at: string;
  updated_at: string | null;
}

interface AiInterviewSessionRowLite {
  id: string;
  candidate_id: string | null;
  job_id: string | null;
  application_id: string | null;
  status: string | null;
  completed_at: string | null;
  created_at: string | null;
  created_by_org_user_id: string | null;
}

interface OutboxEventRowLite {
  id: string;
  event_type: string;
  aggregate_type: string;
  aggregate_id: string;
  payload: Record<string, unknown>;
  created_at: string;
}

interface CandidateNameRow {
  id: string;
  full_name: string | null;
}

function candidateLabel(candidateId: string | null, byId: Map<string, string>): string {
  if (!candidateId) return 'candidate';
  return byId.get(candidateId) ?? 'candidate';
}

/**
 * Map one `ggj_b2b_object_history` row to a timeline event. Returns null
 * when the change isn't interesting (machine-managed column, delete-only,
 * attribute identical to old value).
 */
function mapObjectHistoryRow(
  row: ObjectHistoryRowLite,
  positionId: string,
): TimelineRowSource | null {
  if (REQUISITION_SKIP_ATTRS.has(row.attribute_name)) return null;

  const href = `/requisitions/${positionId}`;
  const baseId = `oh:${row.id}`;

  // Status transitions.
  if (row.attribute_name === 'status') {
    // First insert with status='draft' is not actionable; skip.
    if (row.action === 'insert' && (row.new_value ?? '') === 'draft') return null;
    const to = row.new_value ?? '';
    const from = row.old_value ?? '';
    const isClose = to === 'filled' || to === 'cancelled';
    const isOpen = to === 'sourcing' && from !== 'sourcing';
    return {
      id: baseId,
      ts: row.created_at,
      kind: isClose ? 'position.closed' : isOpen ? 'position.opened' : 'position.status_changed',
      actorOrgUserId: row.changed_by_org_user_id,
      label: isClose
        ? `Position closed (${to})`
        : isOpen
        ? 'Position opened'
        : `Status: ${statusTransitionLabel(from || '—')} → ${statusTransitionLabel(to)}`,
      detail: from && to ? `${from} → ${to}` : undefined,
      viewHref: href,
      icon: isClose ? (to === 'filled' ? 'check' : 'x') : isOpen ? 'plus' : 'edit',
    };
  }

  // Dedicated attribute transitions (opened_at / closed_at) — surface the
  // status-driven counterpart if the status row didn't already.
  if (row.attribute_name === 'opened_at' && row.new_value && !row.old_value) {
    return {
      id: baseId,
      ts: row.created_at,
      kind: 'position.opened',
      actorOrgUserId: row.changed_by_org_user_id,
      label: 'Position opened',
      viewHref: href,
      icon: 'plus',
    };
  }
  if (row.attribute_name === 'closed_at' && row.new_value && !row.old_value) {
    return {
      id: baseId,
      ts: row.created_at,
      kind: 'position.closed',
      actorOrgUserId: row.changed_by_org_user_id,
      label: 'Position closed',
      viewHref: href,
      icon: 'check',
    };
  }
  if (REQUISITION_DEDICATED_ATTRS.has(row.attribute_name)) return null;

  // Ignore the initial INSERT wave — those rows pollute the timeline with
  // dozens of "set title to X" entries for every new requisition.
  if (row.action === 'insert') return null;

  const attrLabel = REQUISITION_ATTR_LABELS[row.attribute_name] ?? row.attribute_name.replace(/_/g, ' ');
  const detail =
    row.old_value && row.new_value
      ? `${row.old_value} → ${row.new_value}`
      : row.new_value
      ? row.new_value
      : row.old_value ?? undefined;

  return {
    id: baseId,
    ts: row.created_at,
    kind: 'position.field_edited',
    actorOrgUserId: row.changed_by_org_user_id,
    label: `Edited ${attrLabel}`,
    detail,
    viewHref: href,
    icon: 'edit',
  };
}

/** Swallow-and-log wrapper — per-source query failures degrade gracefully
 *  (spec: "If any source query fails, log + continue"). */
function logQueryFailure(source: string, err: unknown): void {
  // eslint-disable-next-line no-console
  console.warn(`[reportsTatService.getPositionTimeline] ${source} query failed:`, err);
}

interface ActorResolutionInput {
  orgUserIds: Set<string>;
  keycloakSubs: Set<string>;
}

interface ActorDirectory {
  byId: Map<string, { name: string; avatarUrl: string | null }>;
  bySub: Map<string, { id: string; name: string; avatarUrl: string | null }>;
}

/** Batched actor lookup — one query by UUID list, one by keycloak_sub list. */
async function resolveActors(
  orgId: string,
  input: ActorResolutionInput,
): Promise<ActorDirectory> {
  const byId = new Map<string, { name: string; avatarUrl: string | null }>();
  const bySub = new Map<string, { id: string; name: string; avatarUrl: string | null }>();

  const ids = Array.from(input.orgUserIds);
  const subs = Array.from(input.keycloakSubs);

  await Promise.all([
    ids.length === 0
      ? Promise.resolve()
      : supabase
          .from('ggj_b2b_org_users')
          .select('id, display_name, email, avatar_url, keycloak_sub')
          .eq('org_id', orgId)
          .in('id', ids)
          .then(({ data, error }) => {
            if (error) {
              logQueryFailure('org_users by id', error);
              return;
            }
            for (const u of (data ?? []) as Array<{
              id: string;
              display_name: string | null;
              email: string | null;
              avatar_url: string | null;
              keycloak_sub: string | null;
            }>) {
              const name = u.display_name ?? u.email ?? 'Unknown';
              byId.set(u.id, { name, avatarUrl: u.avatar_url });
              if (u.keycloak_sub) {
                bySub.set(u.keycloak_sub, { id: u.id, name, avatarUrl: u.avatar_url });
              }
            }
          }),
    subs.length === 0
      ? Promise.resolve()
      : supabase
          .from('ggj_b2b_org_users')
          .select('id, display_name, email, avatar_url, keycloak_sub')
          .eq('org_id', orgId)
          .in('keycloak_sub', subs)
          .then(({ data, error }) => {
            if (error) {
              logQueryFailure('org_users by sub', error);
              return;
            }
            for (const u of (data ?? []) as Array<{
              id: string;
              display_name: string | null;
              email: string | null;
              avatar_url: string | null;
              keycloak_sub: string | null;
            }>) {
              const name = u.display_name ?? u.email ?? 'Unknown';
              byId.set(u.id, { name, avatarUrl: u.avatar_url });
              if (u.keycloak_sub) {
                bySub.set(u.keycloak_sub, { id: u.id, name, avatarUrl: u.avatar_url });
              }
            }
          }),
  ]);

  return { byId, bySub };
}

// ── Mock data for demo (remove after demo) ──────────────────────────────────

const DEMO_MOCK_ENABLED = false; // Toggle to disable mock data

const MOCK_OWNERS = [
  { id: 'mock-1', name: 'Priya Kapoor', avatarUrl: null },
  { id: 'mock-2', name: 'Arjun Singh', avatarUrl: null },
  { id: 'mock-3', name: 'Rahul Verma', avatarUrl: null },
  { id: 'mock-4', name: 'Deepika Nair', avatarUrl: null },
  { id: 'mock-5', name: 'Vikram Mehta', avatarUrl: null },
];

const MOCK_CLIENTS = ['TCS', 'Infosys', 'Wipro', 'HCL Technologies', 'Tech Mahindra', 'Cognizant'];

const MOCK_TITLES = [
  'Senior Software Engineer',
  'Product Manager',
  'Data Scientist',
  'DevOps Engineer',
  'Full Stack Developer',
  'Cloud Architect',
  'QA Lead',
  'UX Designer',
  'Mobile Developer',
  'Backend Engineer',
];

const MOCK_STATUSES: Array<{ status: string; stageLabel: string }> = [
  { status: 'sourcing', stageLabel: 'Sourcing (recruiting active)' },
  { status: 'screening', stageLabel: 'Screening' },
  { status: 'interview', stageLabel: 'Interviewing' },
  { status: 'offer', stageLabel: 'Offer stage' },
  { status: 'filled', stageLabel: 'Filled' },
];

function generateMockPositionRows(count: number): PositionTatRow[] {
  const now = new Date();
  const rows: PositionTatRow[] = [];
  
  for (let i = 0; i < count; i++) {
    const openedDaysAgo = Math.floor(Math.random() * 60) + 5;
    const openedAt = new Date(now.getTime() - openedDaysAgo * MS_PER_DAY);
    const targetDays = Math.floor(Math.random() * 30) + 20;
    const targetCloseDate = new Date(openedAt.getTime() + targetDays * MS_PER_DAY);
    
    const isClosed = Math.random() > 0.6;
    const closedAt = isClosed 
      ? new Date(openedAt.getTime() + (Math.floor(Math.random() * targetDays) + 5) * MS_PER_DAY)
      : null;
    
    const statusInfo = MOCK_STATUSES[Math.floor(Math.random() * MOCK_STATUSES.length)];
    const owner = MOCK_OWNERS[Math.floor(Math.random() * MOCK_OWNERS.length)];
    const client = MOCK_CLIENTS[Math.floor(Math.random() * MOCK_CLIENTS.length)];
    const title = MOCK_TITLES[Math.floor(Math.random() * MOCK_TITLES.length)];
    
    const tatDaysActual = closedAt 
      ? Math.floor((closedAt.getTime() - openedAt.getTime()) / MS_PER_DAY)
      : Math.floor((now.getTime() - openedAt.getTime()) / MS_PER_DAY);
    
    const tatBreach = closedAt 
      ? closedAt > targetCloseDate
      : now > targetCloseDate;
    
    rows.push({
      id: `mock-position-${i}`,
      title,
      clientName: client,
      ownerUserId: owner.id,
      ownerName: owner.name,
      status: isClosed ? 'filled' : statusInfo.status,
      stageLabel: isClosed ? 'Filled' : statusInfo.stageLabel,
      openedAt: openedAt.toISOString(),
      closedAt: closedAt?.toISOString() ?? null,
      targetCloseDate: targetCloseDate.toISOString().split('T')[0],
      tatDaysTarget: targetDays,
      tatDaysActual,
      tatBreach,
    });
  }
  
  return rows;
}

function generateMockIndividualRows(): IndividualTatRow[] {
  return MOCK_OWNERS.map((owner, idx) => {
    const activePositions = Math.floor(Math.random() * 8) + 1;
    const avgDaysOpen = Math.floor(Math.random() * 25) + 10;
    const avgTatActual = Math.floor(Math.random() * 20) + 15;
    const breachRate = Math.random() * 0.3;
    const onTimeFillRate = 0.6 + Math.random() * 0.35;
    
    const spark7d = Array.from({ length: 7 }, () => 
      Math.floor(Math.random() * 5) + activePositions - 2
    ).map(v => Math.max(0, v));
    
    return {
      userId: owner.id,
      userName: owner.name,
      avatarUrl: owner.avatarUrl,
      activePositions,
      avgDaysOpen,
      avgTatActual,
      breachRate,
      onTimeFillRate,
      spark7d,
    };
  });
}

// ── Public API ──────────────────────────────────────────────────────────────

export const reportsTatService = {
  /**
   * Per-position TAT rows, windowed by "opened OR closed in range". Sorted
   * ascending by `targetCloseDate` so the most-urgent open positions rise
   * to the top. Stage label is precomputed for the UI.
   */
  async listByPosition(orgId: string, range: DateRange): Promise<PositionTatRow[]> {
    if (!orgId) return [];
    const { fromIso, toIso } = resolveRange(range);

    const rows = await positionTatService.listByOrg(orgId);
    const windowed = rows.filter(
      (r) => inRange(r.openedAt, fromIso, toIso) || inRange(r.closedAt, fromIso, toIso),
    );

    const realRows = windowed.map((r) => ({
      ...r,
      stageLabel:
        REQUISITION_STATUS_LABELS[r.status as RequisitionStatus] ??
        r.status.replace(/_/g, ' '),
    }));

    // Inject mock data for demo if real data is sparse
    if (DEMO_MOCK_ENABLED && realRows.length < 5) {
      const mockRows = generateMockPositionRows(12);
      return [...realRows, ...mockRows];
    }

    return realRows;
  },

  /**
   * One row per owner. Positions without an `approver_user_id` are skipped
   * — they can't be attributed to a recruiter and would otherwise bucket
   * into an "unassigned" phantom row that we don't want in a per-person
   * report.
   */
  async listByIndividual(orgId: string, range: DateRange): Promise<IndividualTatRow[]> {
    if (!orgId) return [];
    const { fromIso, toIso } = resolveRange(range);

    const allRows = await positionTatService.listByOrg(orgId);

    // Pre-compute the 7 end-of-day ms markers (oldest first).
    const today = new Date();
    const dayEnds: number[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setUTCDate(d.getUTCDate() - i);
      dayEnds.push(endOfDayUtc(d));
    }

    const byOwner = new Map<string, BaseTatRow[]>();
    for (const r of allRows) {
      if (!r.ownerUserId) continue;
      const bucket = byOwner.get(r.ownerUserId);
      if (bucket) bucket.push(r);
      else byOwner.set(r.ownerUserId, [r]);
    }

    const ownerIds = Array.from(byOwner.keys());
    const profiles = await fetchOwnerProfiles(orgId, ownerIds);

    const out: IndividualTatRow[] = ownerIds.map((userId) => {
      const rows = byOwner.get(userId) ?? [];
      const profile = profiles.get(userId);
      const agg = aggregateOwner(rows, dayEnds, fromIso, toIso);
      return {
        userId,
        userName: profile?.name ?? 'Unknown',
        avatarUrl: profile?.avatarUrl ?? null,
        ...agg,
      };
    });

    // Inject mock data for demo if real data is sparse
    if (DEMO_MOCK_ENABLED && out.length < 3) {
      const mockRows = generateMockIndividualRows();
      out.push(...mockRows);
    }

    // Stable sort: most-active first, then alphabetical.
    out.sort((a, b) => {
      if (b.activePositions !== a.activePositions) return b.activePositions - a.activePositions;
      return a.userName.localeCompare(b.userName, undefined, { sensitivity: 'base' });
    });

    return out;
  },

  /**
   * Single-root hierarchy:
   *   org
   *    ├── individual (owner A)
   *    ├── individual (owner B)
   *    └── …
   *
   * There's no team layer today because the codebase has no `team_id` /
   * `manager_id` column on `ggj_b2b_org_users`; `settings/OrgHierarchyPage`
   * synthesises its tree from role ordering, which isn't a stable basis
   * for numeric rollups. When a real hierarchy table lands this function
   * is the single place to introduce the intermediate `team` nodes — the
   * UI tree-table already branches on `kind`.
   */
  async listByHierarchy(orgId: string, range: DateRange): Promise<HierarchyTatNode> {
    const individuals = await reportsTatService.listByIndividual(orgId, range);

    const children: HierarchyTatNode[] = individuals.map((ind) => ({
      kind: 'individual',
      id: ind.userId,
      label: ind.userName,
      metrics: {
        activePositions: ind.activePositions,
        avgDaysOpen: ind.avgDaysOpen,
        avgTatActual: ind.avgTatActual,
        breachRate: ind.breachRate,
        onTimeFillRate: ind.onTimeFillRate,
      },
    }));

    return {
      kind: 'org',
      id: orgId,
      label: 'Organisation',
      metrics: rollupOrgMetrics(individuals),
      children,
    };
  },

  /**
   * C-40 · Live-joined position timeline.
   *
   * Merges five source tables into a single chronologically-descending
   * event stream for the given requisition. Every source query is
   * independently try/catch-wrapped — a missing relation or schema-cache
   * miss logs a warning and the timeline ships without that slice rather
   * than throwing. `limit` defaults to 200; `before` lets the drawer
   * paginate older events via infinite scroll.
   */
  async getPositionTimeline(
    positionId: string,
    opts: PositionTimelineOpts = {},
  ): Promise<TimelineEvent[]> {
    if (!positionId) return [];
    const limit = opts.limit && opts.limit > 0 ? opts.limit : 200;
    const before = opts.before;

    // ── Step 1: anchor on the requisition row so we can scope joins ──
    const reqRes = await supabase
      .from('ggj_b2b_requisitions')
      .select('id, org_id, title, job_id, created_job_id')
      .eq('id', positionId)
      .maybeSingle();

    if (reqRes.error) {
      // No anchor row → nothing to join against. Caller sees an empty timeline.
      logQueryFailure('requisition', reqRes.error);
      return [];
    }
    const req = reqRes.data as {
      id: string;
      org_id: string;
      title: string | null;
      job_id: string | null;
      created_job_id: string | null;
    } | null;
    if (!req) return [];

    const orgId = req.org_id;
    const jobIds = uniqueStrings([req.job_id, req.created_job_id]);

    // ── Step 2: fan-out the three top-level source queries in parallel ──
    //   (object_history for the req itself, applications for jobs derived
    //    from it, outbox events for email lifecycle.)
    const [historyRes, appsRes, outboxRes] = await Promise.all([
      supabase
        .from('ggj_b2b_object_history')
        .select(
          'id, record_id, action, attribute_name, old_value, new_value, changed_by_org_user_id, created_at',
        )
        .eq('org_id', orgId)
        .eq('entity_type', 'requisitions')
        .eq('record_id', positionId)
        .order('created_at', { ascending: false })
        .limit(400)
        .then((r) => ({ data: r.data, error: r.error })),
      jobIds.length === 0
        ? Promise.resolve({ data: [] as ApplicationRowLite[], error: null })
        : supabase
            .from('ggj_b2b_applications')
            .select('id, job_id, candidate_id, status, created_at, assigned_to')
            .eq('org_id', orgId)
            .in('job_id', jobIds)
            .then((r) => ({ data: r.data, error: r.error })),
      // Outbox filter: PostgREST supports `payload->>requisition_id` keys
      // via `.filter`. We also union aggregate_type='requisition' to catch
      // events that put the req id on the aggregate rather than payload.
      supabase
        .from('ggj_b2b_outbox_events')
        .select('id, event_type, aggregate_type, aggregate_id, payload, created_at')
        .eq('org_id', orgId)
        .in('event_type', ['email.sent', 'email.sequence.enrolled'])
        .or(`aggregate_id.eq.${positionId},payload->>requisition_id.eq.${positionId}`)
        .order('created_at', { ascending: false })
        .limit(400)
        .then((r) => ({ data: r.data, error: r.error })),
    ]);

    const historyRows: ObjectHistoryRowLite[] =
      historyRes.error || !historyRes.data
        ? (logQueryFailure('object_history', historyRes.error), [])
        : (historyRes.data as unknown as ObjectHistoryRowLite[]);

    const apps: ApplicationRowLite[] =
      appsRes.error || !appsRes.data
        ? (logQueryFailure('applications', appsRes.error), [])
        : (appsRes.data as unknown as ApplicationRowLite[]);

    const outboxRows: OutboxEventRowLite[] =
      outboxRes.error || !outboxRes.data
        ? (logQueryFailure('outbox_events', outboxRes.error), [])
        : (outboxRes.data as unknown as OutboxEventRowLite[]);

    const appIds = apps.map((a) => a.id);
    const candidateIds = uniqueStrings(apps.map((a) => a.candidate_id));

    // ── Step 3: fan-out the per-application source queries in parallel ──
    const [statusHistRes, interviewsRes, aiSessionsRes, candsRes] = await Promise.all([
      appIds.length === 0
        ? Promise.resolve({ data: [] as AppStatusHistoryRowLite[], error: null })
        : supabase
            .from('ggj_b2b_application_status_history')
            .select('id, application_id, from_status, to_status, changed_by, changed_at')
            .in('application_id', appIds)
            .order('changed_at', { ascending: false })
            .limit(400)
            .then((r) => ({ data: r.data, error: r.error })),
      appIds.length === 0
        ? Promise.resolve({ data: [] as InterviewEventRowLite[], error: null })
        : supabase
            .from('ggj_b2b_interview_events')
            .select('id, application_id, round, scheduled_at, status, created_at, updated_at')
            .eq('org_id', orgId)
            .in('application_id', appIds)
            .order('updated_at', { ascending: false })
            .limit(400)
            .then((r) => ({ data: r.data, error: r.error })),
      // AI sessions can be linked via job_id or application_id; query both
      // independently so a missing application_id doesn't exclude job-level rows.
      (async () => {
        const fragments: AiInterviewSessionRowLite[] = [];
        const errors: unknown[] = [];
        if (jobIds.length > 0) {
          const { data, error } = await supabase
            .from('ggj_b2b_ai_interview_sessions')
            .select(
              'id, candidate_id, job_id, application_id, status, completed_at, created_at, created_by_org_user_id',
            )
            .eq('org_id', orgId)
            .in('job_id', jobIds)
            .limit(400);
          if (error) errors.push(error);
          else if (data) fragments.push(...(data as unknown as AiInterviewSessionRowLite[]));
        }
        if (appIds.length > 0) {
          const { data, error } = await supabase
            .from('ggj_b2b_ai_interview_sessions')
            .select(
              'id, candidate_id, job_id, application_id, status, completed_at, created_at, created_by_org_user_id',
            )
            .eq('org_id', orgId)
            .in('application_id', appIds)
            .limit(400);
          if (error) errors.push(error);
          else if (data) fragments.push(...(data as unknown as AiInterviewSessionRowLite[]));
        }
        return { data: fragments, error: errors[0] ?? null };
      })(),
      candidateIds.length === 0
        ? Promise.resolve({ data: [] as CandidateNameRow[], error: null })
        : supabase
            .from('ggj_b2b_candidates')
            .select('id, full_name')
            .eq('org_id', orgId)
            .in('id', candidateIds)
            .then((r) => ({ data: r.data, error: r.error })),
    ]);

    const statusHist: AppStatusHistoryRowLite[] =
      statusHistRes.error || !statusHistRes.data
        ? (logQueryFailure('application_status_history', statusHistRes.error), [])
        : (statusHistRes.data as unknown as AppStatusHistoryRowLite[]);

    const interviews: InterviewEventRowLite[] =
      interviewsRes.error || !interviewsRes.data
        ? (logQueryFailure('interview_events', interviewsRes.error), [])
        : (interviewsRes.data as unknown as InterviewEventRowLite[]);

    const aiSessionsRaw: AiInterviewSessionRowLite[] =
      aiSessionsRes.error || !aiSessionsRes.data
        ? (logQueryFailure('ai_interview_sessions', aiSessionsRes.error), [])
        : aiSessionsRes.data;

    // Dedupe AI sessions — a row can match both job_id and application_id queries.
    const aiSeen = new Set<string>();
    const aiSessions: AiInterviewSessionRowLite[] = [];
    for (const s of aiSessionsRaw) {
      if (aiSeen.has(s.id)) continue;
      aiSeen.add(s.id);
      aiSessions.push(s);
    }

    const candidateNameById = new Map<string, string>();
    if (!candsRes.error && candsRes.data) {
      for (const c of candsRes.data as CandidateNameRow[]) {
        if (c.full_name) candidateNameById.set(c.id, c.full_name);
      }
    } else if (candsRes.error) {
      logQueryFailure('candidates', candsRes.error);
    }

    const applicationById = new Map<string, ApplicationRowLite>();
    for (const a of apps) applicationById.set(a.id, a);

    // ── Step 4: normalise every source row to TimelineRowSource ──
    const merged: TimelineRowSource[] = [];

    for (const h of historyRows) {
      const ev = mapObjectHistoryRow(h, positionId);
      if (ev) merged.push(ev);
    }

    for (const a of apps) {
      if (!a.created_at) continue;
      const name = candidateLabel(a.candidate_id, candidateNameById);
      merged.push({
        id: `app:${a.id}:added`,
        ts: a.created_at,
        kind: 'application.added',
        actorOrgUserId: a.assigned_to,
        label: `Candidate added: ${name}`,
        viewHref: `/applications/${a.id}`,
        icon: 'plus',
      });
    }

    for (const h of statusHist) {
      if (!h.changed_at || !h.to_status) continue;
      const app = applicationById.get(h.application_id);
      if (!app) continue; // org-scope guard (history has no org_id column)
      const name = candidateLabel(app.candidate_id, candidateNameById);
      if (h.to_status === 'shortlisted') {
        merged.push({
          id: `ash:${h.id}:shortlisted`,
          ts: h.changed_at,
          kind: 'application.shortlisted',
          actorKeycloakSub: h.changed_by,
          label: `Shortlisted: ${name}`,
          detail: h.from_status ? `${h.from_status} → ${h.to_status}` : undefined,
          viewHref: `/applications/${app.id}`,
          icon: 'check',
        });
      } else if (h.to_status === 'rejected') {
        merged.push({
          id: `ash:${h.id}:rejected`,
          ts: h.changed_at,
          kind: 'application.rejected',
          actorKeycloakSub: h.changed_by,
          label: `Rejected: ${name}`,
          detail: h.from_status ? `${h.from_status} → ${h.to_status}` : undefined,
          viewHref: `/applications/${app.id}`,
          icon: 'x',
        });
      } else {
        merged.push({
          id: `ash:${h.id}:stage`,
          ts: h.changed_at,
          kind: 'application.stage_changed',
          actorKeycloakSub: h.changed_by,
          label: `${name}: ${h.to_status.replace(/_/g, ' ')}`,
          detail: h.from_status ? `${h.from_status} → ${h.to_status}` : undefined,
          viewHref: `/applications/${app.id}`,
          icon: 'edit',
        });
      }
    }

    for (const iv of interviews) {
      const app = applicationById.get(iv.application_id);
      if (!app) continue;
      const name = candidateLabel(app.candidate_id, candidateNameById);
      const round = iv.round ?? 1;
      const viewHref = `/interviews/app/${iv.application_id}`;

      // "Scheduled" at the row's creation timestamp — that's the moment the
      // recruiter pressed "Schedule" even if scheduled_at moves around later.
      merged.push({
        id: `iv:${iv.id}:scheduled`,
        ts: iv.created_at,
        kind: 'interview.scheduled',
        label: `Interview R${round} scheduled: ${name}`,
        detail: iv.scheduled_at ? `for ${iv.scheduled_at}` : undefined,
        viewHref,
        icon: 'calendar',
      });

      if (iv.status === 'completed') {
        merged.push({
          id: `iv:${iv.id}:held`,
          ts: iv.updated_at ?? iv.scheduled_at ?? iv.created_at,
          kind: 'interview.held',
          label: `Interview R${round} held: ${name}`,
          viewHref,
          icon: 'check',
        });
      } else if (iv.status === 'cancelled' || iv.status === 'no_show') {
        merged.push({
          id: `iv:${iv.id}:outcome`,
          ts: iv.updated_at ?? iv.created_at,
          kind: 'interview.outcome',
          label: `Interview R${round} ${iv.status === 'no_show' ? 'no-show' : 'cancelled'}: ${name}`,
          viewHref,
          icon: 'x',
        });
      }
    }

    for (const s of aiSessions) {
      if (s.status !== 'completed' || !s.completed_at) continue;
      const candName = candidateLabel(s.candidate_id, candidateNameById);
      merged.push({
        id: `ais:${s.id}:completed`,
        ts: s.completed_at,
        kind: 'ai_interview.completed',
        actorOrgUserId: s.created_by_org_user_id,
        label: `AI interview completed: ${candName}`,
        viewHref: `/ai-interviews/${s.id}`,
        icon: 'sparkles',
      });
    }

    for (const o of outboxRows) {
      const kind: TimelineEventKind =
        o.event_type === 'email.sequence.enrolled' ? 'email.sequence.enrolled' : 'email.sent';
      const payload = o.payload ?? {};
      const subject = typeof payload.subject === 'string' ? payload.subject : null;
      const sequenceName =
        typeof payload.sequence_name === 'string' ? payload.sequence_name : null;
      const threadId = typeof payload.thread_id === 'string' ? payload.thread_id : null;
      const actorSub =
        typeof payload.actor_keycloak_sub === 'string'
          ? payload.actor_keycloak_sub
          : typeof payload.sent_by === 'string'
          ? payload.sent_by
          : null;
      merged.push({
        id: `oe:${o.id}:${kind}`,
        ts: o.created_at,
        kind,
        actorKeycloakSub: actorSub,
        label:
          kind === 'email.sequence.enrolled'
            ? `Enrolled in sequence${sequenceName ? `: ${sequenceName}` : ''}`
            : `Email sent${subject ? `: ${subject}` : ''}`,
        viewHref: threadId ? `/emails/thread/${threadId}` : undefined,
        icon: 'mail',
      });
    }

    // ── Step 5: resolve actor names in one batched round-trip ──
    const actorIds = new Set<string>();
    const actorSubs = new Set<string>();
    for (const m of merged) {
      if (m.actorOrgUserId) actorIds.add(m.actorOrgUserId);
      if (m.actorKeycloakSub) actorSubs.add(m.actorKeycloakSub);
    }
    const directory: ActorDirectory =
      actorIds.size === 0 && actorSubs.size === 0
        ? { byId: new Map(), bySub: new Map() }
        : await resolveActors(orgId, { orgUserIds: actorIds, keycloakSubs: actorSubs }).catch((err) => {
            logQueryFailure('actor resolution', err);
            return { byId: new Map(), bySub: new Map() };
          });

    // ── Step 6: sort desc, optional `before` cursor, slice to limit ──
    const beforeMs = before ? new Date(before).getTime() : Number.POSITIVE_INFINITY;
    const ordered = merged
      .filter((m) => new Date(m.ts).getTime() < beforeMs)
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      .slice(0, limit);

    return ordered.map((m) => {
      let actorUserId: string | null = m.actorOrgUserId ?? null;
      let actorName: string | null = null;
      let actorAvatarUrl: string | null = null;

      if (actorUserId) {
        const profile = directory.byId.get(actorUserId);
        if (profile) {
          actorName = profile.name;
          actorAvatarUrl = profile.avatarUrl;
        }
      } else if (m.actorKeycloakSub) {
        const profile = directory.bySub.get(m.actorKeycloakSub);
        if (profile) {
          actorUserId = profile.id;
          actorName = profile.name;
          actorAvatarUrl = profile.avatarUrl;
        }
      }

      const out: TimelineEvent = {
        id: m.id,
        ts: m.ts,
        kind: m.kind,
        actorUserId,
        actorName,
        actorAvatarUrl,
        label: m.label,
        icon: m.icon,
      };
      if (m.detail) out.detail = m.detail;
      if (m.viewHref) out.viewHref = m.viewHref;
      return out;
    });
  },
};

// Average helper exported for tests / callers that need to recompute.
export const __testing = { daysBetween, wasActiveAt, resolveRange };
